// The gated step's look (#1856): a ring, not a spotlight, and an anchored
// card that never takes the caret. Clicks always go through to the page; the
// only way out is «Выйти» (Esc stays the editor's).

import { type MouseEvent, type ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { PopoverAnchor, PopoverContent, PopoverRoot } from '@/components/ui/popover';
import { Spinner } from '@/components/ui/spinner';
import { cn } from '@/lib/utils';
import { CARD_GAP, CARD_HEIGHT, CARD_PAD, CARD_WIDTH, type CardSide, cardSpot, openPopoverRects } from './cardPlacement';
import { type Placement, type TargetMeasure, TOUR_OVERLAY_ATTR } from './targetPlacement';

const RING_PAD = 6;
export const overlayAttr = { [TOUR_OVERLAY_ATTR]: '' };

/** The tutorial's buttons keep the caret where it is: a mousedown that
 *  focused them would blur the paper line the user is writing in. */
const keepCaret = (event: MouseEvent) => event.preventDefault();

interface TourButtonProps {
    onClick: (event: MouseEvent) => void;
    children: ReactNode;
    primary?: boolean;
    testId?: string;
}

export function TourButton({ onClick, children, primary = false, testId }: TourButtonProps) {
    return (
        <Button
            data-testid={testId}
            variant="text"
            size="small"
            onMouseDown={keepCaret}
            onClick={onClick}
            className={cn('h-8 min-w-0 px-2', primary ? 'text-secondary' : 'text-c11 opacity-70 hover:opacity-100')}
        >
            {children}
        </Button>
    );
}

/** Bottom-centre pill: the dock while the target is away, and the status
 *  while the setup settles. */
function Pill({ children, testId }: { children: ReactNode; testId: string }) {
    return (
        <div
            {...overlayAttr}
            data-testid={testId}
            className="fixed bottom-5 left-1/2 z-[3500] flex -translate-x-1/2 items-center gap-3 rounded-full bg-c24 px-5 py-1.5 text-sm text-text-secondary shadow-lg"
        >
            {children}
        </div>
    );
}

interface StepOverlayProps {
    /** «Шаг N из M». */
    progress: string;
    title: string;
    body: ReactNode;
    /** Read every frame: the target follows the engine state. */
    measure: () => TargetMeasure;
    onShow: () => void;
    /** A showcase step's «Далее»: the card and the dock show it only when given. */
    onNext?: () => void;
    onExit: () => void;
}

const viewportSize = () => ({ width: window.innerWidth, height: window.innerHeight });

/** The card's design width (a shrunk card's would feed back into the side it
 *  picks) and its own height, before it mounts a typical one. */
const cardSize = (card: HTMLElement | null) => ({ width: CARD_WIDTH, height: card?.offsetHeight || CARD_HEIGHT });

export function StepOverlay({ progress, title, body, measure, onShow, onNext, onExit }: StepOverlayProps) {
    const { t } = useTranslation();
    const [placement, setPlacement] = useState<Placement>('unresolved');
    const [side, setSide] = useState<CardSide>('right');
    const ringRef = useRef<HTMLDivElement>(null);
    const cardRef = useRef<HTMLDivElement>(null);
    /** What the card hangs off: the target, or it with its open popover. */
    const anchorRef = useRef<DOMRect | null>(null);
    const measureRef = useRef(measure);
    measureRef.current = measure;

    // One loop drives the ring and the card's anchor; React hears only about
    // placement and side changes.
    useEffect(() => {
        let frame = 0;
        const tick = () => {
            const current = measureRef.current();
            const spot = current.rect && cardSpot(current.rect, openPopoverRects(), viewportSize(), cardSize(cardRef.current));
            anchorRef.current = spot?.anchor ?? null;
            if (spot) setSide(spot.side);
            const ring = ringRef.current;
            if (ring && current.rect) {
                const { left, top, width, height } = current.rect;
                ring.style.transform = `translate(${left - RING_PAD}px, ${top - RING_PAD}px)`;
                ring.style.width = `${width + RING_PAD * 2}px`;
                ring.style.height = `${height + RING_PAD * 2}px`;
            }
            setPlacement(current.placement);
            frame = requestAnimationFrame(tick);
        };
        frame = requestAnimationFrame(tick);
        return () => cancelAnimationFrame(frame);
    }, []);

    const virtualRef = useMemo(
        () => ({ current: { getBoundingClientRect: () => anchorRef.current ?? new DOMRect(-9999, -9999, 0, 0) } }),
        [],
    );

    const inView = placement === 'visible' || placement === 'covered';
    const nextButton = onNext && (
        <TourButton
            primary
            testId="tutorial-next"
            // A double click's second click would land on the next step's «Далее».
            onClick={event => event.detail <= 1 && onNext()}
        >
            {t('tutorial.overlay.next')}
        </TourButton>
    );

    return (
        <>
            {inView && (
                <div
                    ref={ringRef}
                    {...overlayAttr}
                    data-testid="tutorial-ring"
                    className="pointer-events-none fixed top-0 left-0 z-[1250] rounded-lg ring-2 ring-c8 outline-4 outline-offset-2 outline-c8/25 transition-[transform,width,height] duration-150 ease-out"
                />
            )}
            {/* Open for the whole step and only hidden while docked: a card that
                re-opened on every return would become the top Radix layer and
                take Esc from a popover the user already had open. */}
            <PopoverRoot open modal={false}>
                <PopoverAnchor virtualRef={virtualRef} />
                <PopoverContent
                    ref={cardRef}
                    {...overlayAttr}
                    data-testid="tutorial-card"
                    side={side}
                    align="start"
                    sideOffset={CARD_GAP}
                    collisionPadding={CARD_PAD}
                    updatePositionStrategy="always"
                    onOpenAutoFocus={event => event.preventDefault()}
                    onCloseAutoFocus={event => event.preventDefault()}
                    onInteractOutside={event => event.preventDefault()}
                    onEscapeKeyDown={event => event.preventDefault()}
                    data-tour-covered={placement === 'covered' ? '' : undefined}
                    className={cn(
                        // Narrows down to CARD_MIN_WIDTH, not overflows, beside a popover in a cramped
                        // window; full width on the first frame, before Radix sets the var.
                        'z-[3500] flex w-[300px] max-w-[max(var(--radix-popover-content-available-width,300px),220px)] flex-col gap-2 bg-c24 p-4 text-sm text-text-secondary transition-opacity',
                        // Faded and click-through: what covers the target (a
                        // menu, a dialog) is often how the step is done.
                        // Otherwise clickable even under a modal popover, whose
                        // layer turns pointer events off on the layers below it
                        // (inline, hence `!`): «Выйти» stays in reach.
                        placement === 'covered' ? 'pointer-events-none opacity-50' : 'pointer-events-auto!',
                        !inView && 'invisible',
                    )}
                >
                    <div className="text-xs opacity-60">{progress}</div>
                    <div className="font-semibold">{title}</div>
                    <div className="leading-snug">{body}</div>
                    {placement === 'covered' && (
                        <div data-testid="tutorial-card-covered" className="text-xs text-warning">
                            {t('tutorial.overlay.covered')}
                        </div>
                    )}
                    <div className="-mx-2 -mb-2 flex justify-between">
                        <TourButton onClick={onExit}>{t('tutorial.overlay.exit')}</TourButton>
                        {nextButton}
                    </div>
                </PopoverContent>
            </PopoverRoot>
            {!inView && (
                <Pill testId="tutorial-dock">
                    <span className="opacity-60">{progress}</span>
                    <span>{title}</span>
                    <TourButton primary onClick={onShow}>
                        {t('tutorial.overlay.show')}
                    </TourButton>
                    {nextButton}
                    <TourButton onClick={onExit}>{t('tutorial.overlay.exit')}</TourButton>
                </Pill>
            )}
        </>
    );
}

/** The setup is running («Готовлю сцену…»), or it gave up. No ring either way. */
export function SettleStatus({ failed, onRetry, onExit }: { failed: boolean; onRetry: () => void; onExit: () => void }) {
    const { t } = useTranslation();
    return (
        <Pill testId="tutorial-settle">
            {failed ? (
                <>
                    <span>{t('tutorial.overlay.settle-failed')}</span>
                    <TourButton primary onClick={onRetry}>
                        {t('tutorial.overlay.retry')}
                    </TourButton>
                    <TourButton onClick={onExit}>{t('tutorial.overlay.exit')}</TourButton>
                </>
            ) : (
                <>
                    <Spinner size={14} />
                    <span>{t('tutorial.overlay.preparing')}</span>
                </>
            )}
        </Pill>
    );
}
